import { NamespacedIconName } from 'src/app/shared/constants/material-icon-name';
export interface CommandItem {
  id: string;
  label: string;
  category: CommandCategory;
  icon: NamespacedIconName;
  route?: string;
  action?: string;
  keywords?: string[];
  usageCount?: number;
  /** Chữ mờ bên phải dòng — nhóm sidebar của mục điều hướng (module-rail.jsx Palette `hint`). */
  hint?: string;
}

export interface RecentItem {
  label: string;
  route: string;
  icon: NamespacedIconName;
  timestamp: number; // Date.now()
}

export type CommandCategory = 'navigation' | 'create' | 'report' | 'search';

/**
 * Khớp `SearchResultItem` (BE, FederatedSearchHandler.cs) — kết quả tìm THỰC THỂ THẬT trong
 * CSDL, khác với `CommandItem` (registry tĩnh viết tay). `entityType` là khoá nhóm hiển thị
 * ("Order", "HouseBill", "Container"...).
 */
export interface SearchResultItem {
  resultType: string;
  entityType: string;
  entityId?: string;
  title: string;
  subtitle: string;
  navigationPath: string;
  iconHint: string;
  lastAccessed?: string;
}

export interface FederatedSearchResponse {
  entityResults: SearchResultItem[];
  recentItems: SearchResultItem[];
  totalResults: number;
}

export const COMMAND_REGISTRY: CommandItem[] = [
  // === CREATE ACTIONS ===
  {
    id: 'create-sales-order',
    label: 'Create Sales Order',
    category: 'create',
    icon: 'add_shopping_cart',
    route: '/sales/order/create',
    action: 'create',
    keywords: ['new', 'order', 'SO'],
  },
  {
    id: 'create-journal',
    label: 'Create Journal Entry',
    category: 'create',
    icon: 'edit_note',
    route: '/finance/manual-journal',
    action: 'create',
    keywords: ['new', 'journal', 'GL', 'entry'],
  },
  {
    id: 'create-invoice',
    label: 'Create Invoice',
    category: 'create',
    icon: 'receipt_long',
    route: '/sales/invoice',
    action: 'create',
    keywords: ['new', 'invoice', 'billing'],
  },
  {
    id: 'create-purchase-order',
    label: 'Create Purchase Order',
    category: 'create',
    icon: 'shopping_bag',
    // Mục "Tạo …" phải trỏ tới màn TẠO. Trỏ vào màn danh sách thì người dùng bấm xong vẫn
    // phải tự tìm nút thêm mới.
    route: '/procurement/sourcing/purchase-order/create',
    action: 'create',
    keywords: ['new', 'PO', 'procurement'],
  },
  {
    id: 'create-production-order',
    label: 'Create Production Order',
    category: 'create',
    icon: 'precision_manufacturing',
    route: '/manufacturing/production-orders',
    action: 'create',
    keywords: ['new', 'production', 'MFG'],
  },
  {
    id: 'create-quotation',
    label: 'Create Quotation',
    category: 'create',
    icon: 'request_quote',
    route: '/sales/quotation',
    action: 'create',
    keywords: ['new', 'quote', 'proposal'],
  },
  {
    id: 'create-customer',
    label: 'Create Customer',
    category: 'create',
    icon: 'person_add',
    route: '/sales/customer',
    action: 'create',
    keywords: ['new', 'customer', 'client'],
  },
  {
    id: 'create-vendor',
    label: 'Create Vendor',
    category: 'create',
    icon: 'store',
    // `/procurement/vendor` chưa bao giờ tồn tại — tuyến thật là danh mục nhà cung cấp.
    route: '/procurement/sourcing/supplier',
    action: 'create',
    keywords: ['new', 'vendor', 'supplier'],
  },

  // === NAVIGATION ===
  {
    id: 'nav-cfo-cockpit',
    label: 'CFO Cockpit',
    category: 'navigation',
    icon: 'dashboard',
    route: '/finance/cfo-cockpit',
    keywords: ['CFO', 'executive', 'financial', 'dashboard'],
  },
  {
    id: 'nav-trial-balance',
    label: 'Trial Balance',
    category: 'navigation',
    icon: 'balance',
    route: '/finance/trial-balance',
    keywords: ['TB', 'GL', 'accounts'],
  },
  {
    id: 'nav-income-statement',
    label: 'Income Statement',
    category: 'navigation',
    icon: 'trending_up',
    route: '/finance/financial-statement',
    keywords: ['P&L', 'profit', 'loss', 'revenue'],
  },
  {
    id: 'nav-chart-of-accounts',
    label: 'Chart of Accounts',
    category: 'navigation',
    icon: 'account_tree',
    route: '/finance/chart-of-accounts',
    keywords: ['COA', 'GL', 'accounts'],
  },
  {
    id: 'nav-sales-orders',
    label: 'Sales Orders',
    category: 'navigation',
    icon: 'receipt',
    route: '/sales/order',
    keywords: ['SO', 'orders', 'sales'],
  },
  {
    id: 'nav-invoices',
    label: 'Invoices',
    category: 'navigation',
    icon: 'receipt_long',
    route: '/sales/invoice',
    keywords: ['AR', 'billing'],
  },
  {
    id: 'nav-inventory',
    label: 'Inventory Status',
    category: 'navigation',
    icon: 'inventory_2',
    route: '/warehouse/inventory-status',
    keywords: ['stock', 'WH', 'warehouse'],
  },
  {
    id: 'nav-production-orders',
    label: 'Production Orders',
    category: 'navigation',
    icon: 'precision_manufacturing',
    route: '/manufacturing/production-orders',
    keywords: ['MFG', 'work orders'],
  },
  {
    id: 'nav-purchase-orders',
    label: 'Purchase Orders',
    category: 'navigation',
    icon: 'shopping_bag',
    route: '/procurement/sourcing/purchase-order',
    keywords: ['PO', 'procurement'],
  },
  {
    id: 'nav-employees',
    label: 'Employees',
    category: 'navigation',
    icon: 'groups',
    route: '/hrm/employees',
    keywords: ['HR', 'staff', 'people'],
  },
  {
    id: 'nav-customers',
    label: 'Customers',
    category: 'navigation',
    icon: 'group',
    route: '/sales/customer',
    keywords: ['clients', 'accounts'],
  },
  {
    id: 'nav-mrp',
    label: 'MRP Run',
    category: 'navigation',
    icon: 'settings_suggest',
    route: '/planning/mrp',
    keywords: ['material', 'planning', 'requirements'],
  },
  {
    id: 'nav-leads',
    label: 'Leads',
    category: 'navigation',
    icon: 'person_search',
    route: '/crm/lead',
    keywords: ['CRM', 'prospects'],
  },
  {
    id: 'nav-mfg-dashboard',
    label: 'Manufacturing Dashboard',
    category: 'navigation',
    icon: 'dashboard',
    route: '/manufacturing/manufacturing-dashboard',
    keywords: ['MFG', 'OEE', 'production'],
  },
  {
    id: 'nav-executive-cockpit',
    label: 'Executive Cockpit',
    category: 'navigation',
    icon: 'analytics',
    route: '/finance/executive-cockpit',
    keywords: ['KPI', 'executive', 'management'],
  },
  {
    id: 'nav-users',
    label: 'User Management',
    category: 'navigation',
    icon: 'manage_accounts',
    route: '/administration/user',
    keywords: ['admin', 'users', 'roles'],
  },
  {
    id: 'nav-monitoring',
    label: 'System Monitoring',
    category: 'navigation',
    icon: 'monitor_heart',
    route: '/administration/monitoring',
    keywords: ['health', 'alerts', 'system'],
  },

  // === REPORTS ===
  {
    id: 'rpt-balance-sheet',
    label: 'Balance Sheet',
    category: 'report',
    icon: 'summarize',
    route: '/finance/financial-statement',
    keywords: ['BS', 'assets', 'liabilities'],
  },
  {
    id: 'rpt-ar-aging',
    label: 'AR Aging Report',
    category: 'report',
    icon: 'schedule',
    route: '/sales/ar-aging',
    keywords: ['receivables', 'aging', 'collections'],
  },
  {
    id: 'rpt-margin',
    label: 'Margin Dashboard',
    category: 'report',
    icon: 'pie_chart',
    route: '/finance/margin-dashboard',
    keywords: ['margin', 'profit', 'analysis'],
  },
  {
    id: 'rpt-cash-forecast',
    label: 'Cash Forecast',
    category: 'report',
    icon: 'savings',
    route: '/finance/cash-forecast',
    keywords: ['cash', 'forecast', 'treasury'],
  },
];
